/**
 * Payment mismatch contract (ADR 0031): a bank line smaller than the bill's open payments and
 * outside the ±2% match of each of them. Reconciliation refuses it with
 * `RECONCILIATION_PAYMENT_MISMATCH` and the details below; the user answers with a
 * `PaymentMismatchDecision` for that bill, and reconciliation cancels the payments and handles
 * the remainder by the ADR's rules.
 *
 * Every code here is part of `RECONCILIATION_GL_POSTING_ERROR_CODES`, and the messages record is
 * total, so a new code without pt-BR copy does not compile. Callers classify by the code, never by
 * the message text.
 */

export const PAYMENT_MISMATCH_REFUSAL_CODES = [
  /** No decision for this bill yet: the details carry what the dialog shows. */
  'RECONCILIATION_PAYMENT_MISMATCH',
  /** The bill's payments changed since the user saw them. */
  'RECONCILIATION_PAYMENT_MISMATCH_STALE',
  /** A paid or processing payment is replaced only with a written reason. */
  'RECONCILIATION_PAYMENT_MISMATCH_JUSTIFICATION_REQUIRED',
  /** A payment held by the bank is replaced only after the user cancelled it there. */
  'RECONCILIATION_PAYMENT_MISMATCH_BANK_CANCELLATION_REQUIRED',
  /** An approved bill always schedules the remainder; before approval the user must choose. */
  'RECONCILIATION_PAYMENT_MISMATCH_REMAINDER_INVALID',
  /** The payment already has journal or settlement evidence and cannot be cancelled here. */
  'RECONCILIATION_PAYMENT_MISMATCH_PAYMENT_POSTED',
  /** A send to the bank has not returned yet; its answer would bring the payment back. */
  'RECONCILIATION_PAYMENT_MISMATCH_SEND_IN_FLIGHT',
  /** Replacing a confirmed or bank-held payment needs the AP permission to cancel payments. */
  'RECONCILIATION_PAYMENT_MISMATCH_PERMISSION_REQUIRED',
] as const;

export type PaymentMismatchRefusalCode = (typeof PAYMENT_MISMATCH_REFUSAL_CODES)[number];

export function isPaymentMismatchRefusalCode(value: unknown): value is PaymentMismatchRefusalCode {
  return (
    typeof value === 'string' &&
    (PAYMENT_MISMATCH_REFUSAL_CODES as readonly string[]).includes(value)
  );
}

export const PAYMENT_MISMATCH_JUSTIFICATION_MIN_LENGTH = 10;

export type PaymentMismatchRemainder = 'LEAVE_OPEN' | 'SCHEDULE';

/** Before approval the user chooses the remainder; once approved it is always scheduled. */
export type PaymentMismatchCase = 'BEFORE_APPROVAL' | 'APPROVED';

/** Bill statuses before approval. Every other active status, PROCESSING_APPROVAL included, is approved. */
export const PAYMENT_MISMATCH_BEFORE_APPROVAL_BILL_STATUSES: readonly string[] = [
  'DRAFT',
  'SUBMITTED',
  'PENDING_APPROVAL',
  'REJECTED',
];

export function resolvePaymentMismatchCase(billStatus: string): PaymentMismatchCase {
  return PAYMENT_MISMATCH_BEFORE_APPROVAL_BILL_STATUSES.includes(billStatus)
    ? 'BEFORE_APPROVAL'
    : 'APPROVED';
}

/** Payment statuses that need a written reason: the bank already confirmed or is moving the money. */
export const PAYMENT_MISMATCH_CONFIRMED_PAYMENT_STATUSES: readonly string[] = [
  'PAID',
  'PROCESSING',
];

/** One open payment as the user saw it. The stale check compares all four fields. */
export interface PaymentMismatchSeenPayment {
  paymentId: string;
  status: string;
  /** Scheduled amount in the functional currency. */
  amount: number;
  heldByBank: boolean;
}

/**
 * Replacing a confirmed or bank-held payment cancels a payment the bank already has, so it needs
 * the AP permission to cancel payments on top of reconciliation access. Scheduled and pending
 * payments do not: reconciliation could already cancel those before ADR 0031.
 */
export const PAYMENT_MISMATCH_REPLACE_CONFIRMED_PERMISSION = 'ap:payment:delete';

export function paymentMismatchNeedsReplaceConfirmedPermission(
  payments: readonly Pick<PaymentMismatchSeenPayment, 'status' | 'heldByBank'>[],
): boolean {
  return payments.some(
    (p) => p.heldByBank || PAYMENT_MISMATCH_CONFIRMED_PAYMENT_STATUSES.includes(p.status),
  );
}

/** A payment the dialog lists, as the API reports it. */
export interface PaymentMismatchPayment extends PaymentMismatchSeenPayment {
  paymentMethod: string | null;
  /** `YYYY-MM-DD` */
  scheduledDate: string;
}

/** The details of a `RECONCILIATION_PAYMENT_MISMATCH` refusal. */
export interface PaymentMismatchDetails {
  billId: string;
  /** The vendor's bill number ("NF 86"), so a screen can name the bill without loading it. */
  billNumber: string | null;
  vendorName: string | null;
  case: PaymentMismatchCase;
  /** ISO 4217 code of every amount here: the functional currency of the bill's book. */
  currency: string;
  bankAmount: number;
  remainderAmount: number;
  payments: PaymentMismatchPayment[];
}

/** The user's answer for one bill. */
export interface PaymentMismatchDecision {
  billId: string;
  remainder: PaymentMismatchRemainder;
  /** Required when a payment is paid or processing; at least `PAYMENT_MISMATCH_JUSTIFICATION_MIN_LENGTH` chars. */
  justification?: string;
  /** Required when a payment is held by the bank. */
  bankCancellationConfirmed?: boolean;
  seenPayments: PaymentMismatchSeenPayment[];
}

/** Localized, actionable copy for every payment mismatch refusal. Total by construction. */
export const PAYMENT_MISMATCH_REFUSAL_MESSAGES: Record<
  PaymentMismatchRefusalCode,
  { en: string; ptBr: string }
> = {
  RECONCILIATION_PAYMENT_MISMATCH: {
    en: 'The bank amount does not match the payments on this bill. Choose what to do with the existing payments.',
    ptBr: 'O valor do banco não bate com os pagamentos da conta. Escolha o que fazer com os pagamentos existentes.',
  },
  RECONCILIATION_PAYMENT_MISMATCH_STALE: {
    en: 'The bill changed since you opened this screen. Close and open the reconciliation again.',
    ptBr: 'A conta mudou desde que você abriu esta tela. Feche e abra a conciliação de novo.',
  },
  RECONCILIATION_PAYMENT_MISMATCH_JUSTIFICATION_REQUIRED: {
    en: 'This bill has a payment confirmed by the bank. Write a reason of at least 10 characters to replace it.',
    ptBr: 'Essa conta tem um pagamento confirmado pelo banco. Escreva um motivo com pelo menos 10 caracteres para substituí-lo.',
  },
  RECONCILIATION_PAYMENT_MISMATCH_BANK_CANCELLATION_REQUIRED: {
    en: 'This payment was sent to the bank. Cancel it at the bank, then confirm that you did.',
    ptBr: 'Esse pagamento foi enviado ao banco. Cancele no banco e confirme que cancelou.',
  },
  RECONCILIATION_PAYMENT_MISMATCH_REMAINDER_INVALID: {
    en: 'An approved bill must schedule the remaining amount as a new payment.',
    ptBr: 'Uma conta aprovada precisa agendar o saldo restante como um novo pagamento.',
  },
  RECONCILIATION_PAYMENT_MISMATCH_PAYMENT_POSTED: {
    en: 'This payment already has accounting entries and cannot be replaced here. Edit the payments on the bill to match the bank amount.',
    ptBr: 'Esse pagamento já tem lançamentos contábeis e não pode ser substituído aqui. Edite os pagamentos da conta para bater com o valor do banco.',
  },
  RECONCILIATION_PAYMENT_MISMATCH_SEND_IN_FLIGHT: {
    en: 'This payment is being sent to the bank right now. Wait until the send finishes, then reconcile again.',
    ptBr: 'Esse pagamento está sendo enviado ao banco agora. Espere o envio terminar e concilie de novo.',
  },
  RECONCILIATION_PAYMENT_MISMATCH_PERMISSION_REQUIRED: {
    en: 'To replace a payment that is confirmed or held by the bank, you need the permission to cancel Accounts Payable payments. Ask someone who has it to finish this reconciliation. Nothing was changed.',
    ptBr: 'Para substituir um pagamento confirmado ou que está no banco, você precisa da permissão de cancelar pagamentos do Contas a Pagar. Peça a quem tem essa permissão para concluir esta conciliação. Nada foi alterado.',
  },
};
